import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { getApiBaseUrl } from '../utils/urlHelper';

const API = getApiBaseUrl();
const ClientAuthContext = createContext();

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) throw new Error('useClientAuth must be used within ClientAuthProvider');
  return context;
};

export const ClientAuthProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether the active session is a forced-change session (admin
  // generated a temp password and the user hasn't reset it yet). When true,
  // the app shell renders a blocking modal until the user picks a new one.
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const refreshTimer = useRef(null);

  const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem('client-token', accessToken);
    if (refreshToken) localStorage.setItem('client-refresh-token', refreshToken);
  };

  const clearTokens = () => {
    localStorage.removeItem('client-token');
    localStorage.removeItem('client-refresh-token');
    localStorage.removeItem('client-data');
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  };

  const tryRefreshToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('client-refresh-token');
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API}/api/client/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setToken(data.access_token);
      saveTokens(data.access_token, data.refresh_token);
      scheduleRefresh();
      return true;
    } catch {
      return false;
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    // Refresh 1 day before access token expires (token lasts 7 days, refresh at 6 days)
    refreshTimer.current = setTimeout(() => {
      tryRefreshToken();
    }, 6 * 24 * 60 * 60 * 1000);
  }, [tryRefreshToken]);

  useEffect(() => {
    const savedToken = localStorage.getItem('client-token');
    const savedClient = localStorage.getItem('client-data');
    const savedMcp = localStorage.getItem('client-must-change-password') === '1';
    if (savedToken && savedClient) {
      // Check if token is still valid by decoding exp
      try {
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          // Token expired — try refresh
          tryRefreshToken().then((ok) => {
            if (ok) {
              setClient(JSON.parse(savedClient));
              if (savedMcp) setMustChangePassword(true);
            } else {
              clearTokens();
            }
            setLoading(false);
          });
          return;
        }
      } catch { /* old token without exp — still valid */ }
      setToken(savedToken);
      setClient(JSON.parse(savedClient));
      if (savedMcp) setMustChangePassword(true);
      // Re-verify the flag against the server in the background so we never
      // serve a stale "no longer required" or "newly required" state after a
      // hard refresh. We don't block the boot on this.
      fetch(`${API}/api/client/profile`, {
        headers: { Authorization: `Bearer ${savedToken}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(p => {
          if (!p) return;
          const serverMcp = !!p.must_change_password;
          setMustChangePassword(serverMcp);
          if (serverMcp) localStorage.setItem('client-must-change-password', '1');
          else localStorage.removeItem('client-must-change-password');
        })
        .catch(() => {});
      scheduleRefresh();
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.clientLogin({ email, password });
      setToken(res.data.access_token);
      setClient(res.data.client);
      saveTokens(res.data.access_token, res.data.refresh_token);
      localStorage.setItem('client-data', JSON.stringify(res.data.client));
      const mcp = !!res.data.must_change_password;
      setMustChangePassword(mcp);
      if (mcp) localStorage.setItem('client-must-change-password', '1');
      else localStorage.removeItem('client-must-change-password');
      scheduleRefresh();
      return { success: true, must_change_password: mcp };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erro ao fazer login' };
    }
  };

  const register = async (firstName, lastName, email, phone, password, preferredLanguage = 'pt') => {
    try {
      const res = await api.clientRegister({ first_name: firstName, last_name: lastName, email, phone, password, preferred_language: preferredLanguage });
      setToken(res.data.access_token);
      setClient(res.data.client);
      saveTokens(res.data.access_token, res.data.refresh_token);
      localStorage.setItem('client-data', JSON.stringify(res.data.client));
      scheduleRefresh();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erro ao cadastrar' };
    }
  };

  const updateClient = (updates) => {
    setClient(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('client-data', JSON.stringify(updated));
      return updated;
    });
  };

  const loginWith = (accessToken, clientData, refreshToken = null) => {
    setToken(accessToken);
    setClient(clientData);
    saveTokens(accessToken, refreshToken);
    localStorage.setItem('client-data', JSON.stringify(clientData));
  };

  const logout = () => {
    setToken(null);
    setClient(null);
    setMustChangePassword(false);
    localStorage.removeItem('client-must-change-password');
    clearTokens();
  };

  const getClientHeader = () => {
    if (!token) return {};
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const refreshProfile = async () => {
    if (!token) return;
    try {
      const res = await api.clientProfile({ headers: { Authorization: `Bearer ${token}` } });
      setClient(res.data);
      localStorage.setItem('client-data', JSON.stringify(res.data));
      if (typeof res.data?.must_change_password === 'boolean') {
        setMustChangePassword(res.data.must_change_password);
        if (res.data.must_change_password) localStorage.setItem('client-must-change-password', '1');
        else localStorage.removeItem('client-must-change-password');
      }
    } catch { /* ignore */ }
  };

  // Called by the forced-change modal (and the voluntary "Change password"
  // action in the profile page). On success, clears the must-change flag so
  // the app shell unblocks the UI; no re-login required since the session is
  // already authenticated.
  const changePassword = async (currentPassword, newPassword) => {
    if (!token) return { success: false, error: 'Sessão inválida' };
    try {
      const res = await fetch(`${API}/api/client/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { success: false, error: data.detail || 'Erro ao alterar senha' };
      setMustChangePassword(false);
      localStorage.removeItem('client-must-change-password');
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.message || 'Erro de rede' };
    }
  };

  return (
    <ClientAuthContext.Provider value={{ client, token, loading, login, loginWith, register, logout, updateClient, getClientHeader, refreshProfile, changePassword, mustChangePassword, isAuthenticated: !!token }}>
      {children}
    </ClientAuthContext.Provider>
  );
};
